import React, { useEffect, useRef, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { CountUp } from 'countup.js';
import GemImage from 'src/components/GemImage';
import { VipRatingDetails } from 'src/types/VIP';
import { hexToRgb, numComma } from 'src/utils/common';
const Root = styled.div`
  margin-top: 60px;
  padding-bottom: 40px;
`;
const ProgressBar = styled.div`
  height: 3px;
  background-color: ${({
  theme
}) => `rgba(${hexToRgb(theme.color.surface12)},0.2)`};
  position: relative;
  border-radius: 5px;
`;
const ProgressDesc = styled.div`
  font-size: 10px;
  line-height: 12px;
  color: ${({
  theme
}) => theme.color.content9};
  display: grid;
  grid-gap: 1px;
  grid-template-columns: 10px 1fr;
`;
const ProgressDescIcon = styled(GemImage)`
  width: 10px;
  height: 10px;
`;
const fillAnimation = (percent: number) => keyframes`
  from {
    width: 0%;
  } to {
    width: ${percent}%;
  }
`;
const ProgressBarInner = styled.div<{
  percent: number;
}>`
  position: absolute;
  background-color: ${({
  theme
}) => theme.color.surface12};
  height: 3px;
  ${({
  theme
}) => theme.isRtl ? 'right' : 'left'}: 0px;
  border-radius: 5px;
  animation-name: ${({
  percent
}) => fillAnimation(percent)};
  animation-fill-mode: forwards;
  animation-duration: 0.8s;
`;
const fadeIn = keyframes`
  from {
    opacity: 0;
  } to {
    opacity: 1;
  }
`;
const ProgressCurrentAmount = styled.div<{
  percent: number;
  amountPosition?: 'start' | 'end';
}>`
  position: absolute;
  bottom: 10px;
  background-color: ${({
  theme
}) => theme.color.surface12};
  border-radius: 8px;
  padding: 6px 8px;
  display: grid;
  grid-gap: 4px;
  grid-template-columns: 12px 1fr;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  color: ${({
  theme
}) => theme.color.contentBright};
  ${({
  amountPosition,
  percent,
  theme
}) => {
  if (!amountPosition) {
    return `
        ${theme.isRtl ? 'right' : 'left'}: ${percent}%;
        transform: translateX(${theme.isRtl ? '' : '-'}50%);
      `;
  } else if (amountPosition === 'start') {
    return `
        left: 0px;
      `;
  } else if (amountPosition === 'end') {
    return `
        right: 0px;
      `;
  }
}}
  animation-name: ${fadeIn};
  animation-fill-mode: forwards;
  opacity: 0;
  animation-duration: 0.6s;
  animation-delay: 0.8s;
`;
const ProgressGemIcon = styled(GemImage)`
  width: 12px;
  height: 12px;
`;
const ProgressDotRating = styled.div`
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  color: ${({
  theme
}) => theme.color.content11};
  margin-bottom: 2px;
`;
const ProgressDotIconBase = css`
  width: 14px;
  height: 14px;
  margin-top: -2px;
`;
const ProgressDotIcon = styled.img`
  ${ProgressDotIconBase};
`;
const ProgressDotIconSource = styled.source`
  ${ProgressDotIconBase};
`;
const ProgressDotWrap = styled.div<{
  percent: number;
}>`
  display: flex;
  position: absolute;
  top: 0px;
  flex-direction: column;
  &:before {
    display: flex;
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 5px;
    margin-top: -1px;
    margin-bottom: 7px;
  }
  ${({
  theme,
  percent
}) => {
  if (percent === 0) {
    return `
        ${theme.isRtl ? 'right' : 'left'}: 0px;
        align-items: flex-start;
      `;
  }
  if (percent === 100) {
    return `
        ${!theme.isRtl ? 'right' : 'left'}: 0px;
        align-items: flex-end;
      `;
  }
  return `
      &:before {
        background-color: ${theme.color.surface12};
      }
      ${theme.isRtl ? 'right' : 'left'}: ${percent}%;
      transform: translateX(${theme.isRtl ? '' : '-'}50%);
      align-items: center;
    `;
}}
`;
const dotAnimation = keyframes`
  from {
    width: 0px;
    height: 0px;
  } 50% {
    width: 15px;
    height: 15px;
  } to {
    width: 0px;
    height: 0px;
  }
`;
const ProgressCurrentDot = styled.div`
  background-color: ${({
  theme
}) => theme.color.surface12};
  display: flex;
  width: 7px;
  height: 7px;
  border-radius: 7px;
  transform: translate(${({
  theme
}) => `${theme.isRtl ? '-' : ''}50%`}, -50%);
  margin-top: 1.5px;
  ${({
  theme
}) => !theme.isRtl ? 'right' : 'left'}: 0px;
  position: absolute;
`;
const ProgressCurrentDotBack = styled(ProgressCurrentDot)`
  width: 0px;
  height: 0px;
  border-radius: 15px;
  background-color: ${({
  theme
}) => theme.color.surface12};
  opacity: 0.2;
  animation-name: ${dotAnimation};
  animation-iteration-count: infinite;
  animation-duration: 3s;
`;
const ProgressGemAmount = styled.div`
  white-space: nowrap;
`;
const PictureWrap = styled.picture`
  height: 16px;
  width: 14px;
  margin-top: -2px;
  margin-bottom: 2px;
`;
type Props = Pick<VipRatingDetails, 'ratings' | 'userRating'>;
const Progress: React.FC<Props> = ({
  ratings,
  userRating
}) => {
  const amountRef = useRef<HTMLDivElement | null>(null);
  const [amountPosition, setAmountPosition] = useState<undefined | 'start' | 'end'>(undefined);
  const countUpRef = useRef<CountUp | null>(null);
  const reverseIndex = ratings.findIndex((_, i) => ratings[ratings.length - i - 1].gemAmount <= userRating.purchaseGemAmount);
  const currentIndex = ratings.length - 1 - reverseIndex;
  const current = ratings[currentIndex];
  const next = ratings.find(({
    rating
  }) => rating === userRating.nextRatingGoal);
  const max = next ? next.gemAmount - current.gemAmount : 0;
  const percent = !next ? 100 : (currentIndex + (max - userRating.remainingGemAmount) / max) / (ratings.length - 1) * 100;
  useEffect(() => {
    const dom = amountRef.current;
    const parent = dom?.parentElement;
    if (dom && parent && percent !== undefined) {
      const handler = () => {
        if (dom.offsetLeft < parent.offsetLeft) {
          setAmountPosition('start');
        } else if (dom.offsetLeft >= parent.offsetWidth - dom.offsetWidth) {
          setAmountPosition('end');
        } else {
          setAmountPosition(undefined);
        }
      };
      window.addEventListener('resize', handler);
      handler();
      return () => {
        window.removeEventListener('resize', handler);
      };
    }
  }, [percent]);
  useEffect(() => {
    if (countUpRef.current && userRating.purchaseGemAmount) {
      const el = countUpRef.current;
      const timerId = setTimeout(() => {
        el.update(userRating.purchaseGemAmount);
        el.start();
      }, 900);
      return () => {
        clearInterval(timerId);
      };
    }
  }, [userRating.purchaseGemAmount]);
  return <Root data-sentry-element="Root" data-sentry-component="Progress" data-sentry-source-file="Progress.tsx">
      <ProgressBar data-sentry-element="ProgressBar" data-sentry-source-file="Progress.tsx">
        {ratings.map(({
        rating,
        gemAmount
      }, i) => <ProgressDotWrap percent={i / (ratings.length - 1) * 100} key={`Progress-rating-${rating}`}>
            {i !== 0 ? <ProgressDotRating>{rating}</ProgressDotRating> : <PictureWrap>
                <ProgressDotIconSource srcSet='/images/vip/crown.webp' type='image/webp' />
                <ProgressDotIconSource srcSet='/images/vip/crown.gif' type='image/gif' />
                <ProgressDotIcon src='/images/vip/crown.gif' alt={`Click to check Benefits - ${rating}`} />
              </PictureWrap>}
            <ProgressDesc>
              <ProgressDescIcon />
              {numComma(gemAmount)}
            </ProgressDesc>
          </ProgressDotWrap>)}
        <ProgressBarInner percent={percent} data-sentry-element="ProgressBarInner" data-sentry-source-file="Progress.tsx">
          <ProgressCurrentDot data-sentry-element="ProgressCurrentDot" data-sentry-source-file="Progress.tsx" />
          <ProgressCurrentDotBack data-sentry-element="ProgressCurrentDotBack" data-sentry-source-file="Progress.tsx" />
        </ProgressBarInner>
        <ProgressCurrentAmount percent={percent} ref={amountRef} amountPosition={amountPosition} data-sentry-element="ProgressCurrentAmount" data-sentry-source-file="Progress.tsx">
          <ProgressGemIcon data-sentry-element="ProgressGemIcon" data-sentry-source-file="Progress.tsx" />
          <ProgressGemAmount ref={el => {
          countUpRef.current = el && new CountUp(el, 0);
        }} data-sentry-element="ProgressGemAmount" data-sentry-source-file="Progress.tsx">
            {numComma(userRating.purchaseGemAmount)}
          </ProgressGemAmount>
        </ProgressCurrentAmount>
      </ProgressBar>
    </Root>;
};
export default React.memo(Progress);